import memoizee from 'memoizee';
import { dateFromDateAndHHMM, dateFromHHMM, dateFromYYYYMMDD, dateFromYYYYMMDDAndHHMM, dateToTimeOnly, isSameUTCMonthOrBefore } from './DateUtils';
import { getFirstDayOfWeek, weekdayIsBetween, WEEKDAYS } from './WeekdayUtils';
/** Get all openings and exceptions which apply to this date */
export function getDateMatches(testDate, calendar) {
    var testDateDayStart = new Date(testDate.getFullYear(), testDate.getMonth(), testDate.getDate());
    return {
        openings: calendar.normalHours.filter(function (opening) {
            return weekdayIsBetween(testDate, opening.startDay, opening.endDay);
        }),
        exceptions: calendar.exceptions.filter(function (exception) {
            return (dateFromYYYYMMDD(exception.startDate) <= testDateDayStart &&
                testDateDayStart <= dateFromYYYYMMDD(exception.endDate));
        })
    };
}
/** Get the current exceptional opening, if any */
export function getCurrentExceptionalOpening(testDateTime, exception) {
    for (var _i = 0, _a = exception.openings; _i < _a.length; _i++) {
        var opening = _a[_i];
        if (dateFromYYYYMMDDAndHHMM(opening.startDate, opening.startTime) <=
            testDateTime &&
            testDateTime <= dateFromYYYYMMDDAndHHMM(opening.endDate, opening.endTime)) {
            return opening;
        }
    }
    return null;
}
/** Get the next exceptional opening on the same date, if any */
export function getNextExceptionalOpening(testDateTime, exception) {
    var min = null;
    var minDate = null;
    for (var _i = 0, _a = exception.openings; _i < _a.length; _i++) {
        var opening = _a[_i];
        if (testDateTime <=
            dateFromYYYYMMDDAndHHMM(opening.startDate, opening.startTime) &&
            (minDate === null ||
                minDate >=
                    dateFromYYYYMMDDAndHHMM(opening.startDate, opening.startTime))) {
            min = opening;
            minDate = dateFromYYYYMMDDAndHHMM(opening.startDate, opening.startTime);
        }
    }
    return min;
}
function isInSingleDayNormalOpening(weekday, testWeekday, testDateTime, startTimeRel, endTimeRel) {
    var testTime = dateToTimeOnly(testDateTime);
    // really only spans one day
    if (startTimeRel <= endTimeRel) {
        return (testWeekday === weekday &&
            testTime >= startTimeRel &&
            testTime <= endTimeRel);
    }
    // wraps around the week
    return (weekday !== testWeekday ||
        testTime <= endTimeRel ||
        testTime >= startTimeRel);
}
function isMiddleDayInRange(startWeekday, endWeekday, testWeekday) {
    var endWeekdayAdjusted = startWeekday > endWeekday ? endWeekday + 7 : endWeekday;
    var currentWeekdayAdjusted = startWeekday > testWeekday ? testWeekday + 7 : testWeekday;
    // between both ends
    return (startWeekday < currentWeekdayAdjusted &&
        currentWeekdayAdjusted < endWeekdayAdjusted);
}
/** Get the current normal opening, if any */
export function getCurrentNormalOpening(testDateTime, openings) {
    var currentWeekday = testDateTime.getDay();
    var testTime = dateToTimeOnly(testDateTime);
    for (var _i = 0, openings_1 = openings; _i < openings_1.length; _i++) {
        var opening = openings_1[_i];
        var startWeekday = WEEKDAYS[opening.startDay];
        var endWeekday = WEEKDAYS[opening.endDay];
        var startTimeRel = dateFromHHMM(opening.startTime);
        var endTimeRel = dateFromHHMM(opening.endTime);
        // single-day interval
        if (startWeekday === endWeekday &&
            isInSingleDayNormalOpening(startWeekday, currentWeekday, testTime, startTimeRel, endTimeRel)) {
            return opening;
        }
        // between both ends
        if (isMiddleDayInRange(startWeekday, endWeekday, currentWeekday)) {
            return opening;
        }
        // first day of interval
        if (currentWeekday === startWeekday &&
            currentWeekday !== endWeekday &&
            testTime >= startTimeRel) {
            return opening;
        }
        // last day of interval
        if (currentWeekday !== startWeekday &&
            currentWeekday === endWeekday &&
            testTime <= endTimeRel) {
            return opening;
        }
    }
    return null;
}
/** Get the next normal opening within the same day */
export function getNextNormalOpening(testDateTime, openings) {
    var min = null;
    var minDate = null;
    // no need to handle the potential for the next one being before the current day
    // therefore, we only check startDay = current day and minimize startDay within that subset
    for (var _i = 0, openings_2 = openings; _i < openings_2.length; _i++) {
        var opening = openings_2[_i];
        var openingTime = dateFromDateAndHHMM(testDateTime, opening.startTime);
        if (WEEKDAYS[opening.startDay] === testDateTime.getDay() &&
            testDateTime < openingTime &&
            (minDate === null || minDate > openingTime)) {
            min = opening;
            minDate = openingTime;
        }
    }
    return min;
}
/**
 * Determine if a calendar is open 24/7
 * This will not check for 24/7 calendars that have more than a single opening,
 * even if it would be 24/7.
 */
export function isOpen247(openings) {
    if (openings.length !== 1) {
        return false;
    }
    var opening = openings[0];
    // same day
    if (opening.startDay === opening.endDay) {
        // M 00:00 to M 23:59 should NOT wrap around in this case
        // cases like M 08:00 -> M 07:59 should wrap
        var shifted = dateFromHHMM(opening.endTime);
        shifted.setMinutes(shifted.getMinutes() + 1);
        var startTimeDate = dateFromHHMM(opening.startTime);
        return (shifted.getHours() === startTimeDate.getHours() &&
            shifted.getMinutes() === startTimeDate.getMinutes() &&
            opening.startTime.substring(0, 5) !== '00:00');
    }
    // across day boundary
    return ((WEEKDAYS[opening.endDay] + 1) % 7 === WEEKDAYS[opening.startDay] &&
        opening.endTime.substring(0, 5) === '23:59' &&
        opening.startTime.substring(0, 5) === '00:00');
}
/** Get an array of dates to use for a calendar */
export var getDateArray = memoizee(function (locale, monthBasis) {
    // start of the month
    var date = new Date(Date.UTC(monthBasis.getUTCFullYear(), monthBasis.getUTCMonth(), 1));
    // if the month starts at the beginning of the week, add a full row above of the previous month
    if (date.getUTCDay() === getFirstDayOfWeek(locale)) {
        date.setUTCDate(date.getUTCDate() - 7);
    }
    var firstWeekday = getFirstDayOfWeek(locale);
    // ensure startDate starts at the beginning of a week
    date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() - firstWeekday + 7) % 7));
    // at this point, date must be in a month before `month`.
    // generate this month to the next
    var displayDates = [];
    var week = [];
    do {
        week.push(new Date(date));
        date.setUTCDate(date.getUTCDate() + 1);
        if (week.length === 7) {
            displayDates.push.apply(displayDates, week);
            week = [];
        }
    } while (isSameUTCMonthOrBefore(date, monthBasis));
    while (week.length < 7) {
        week.push(new Date(date));
        date.setUTCDate(date.getUTCDate() + 1);
    }
    displayDates.push.apply(displayDates, week);
    return displayDates;
});
